import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Pagination,
  PaginationItem,
  Paper,
  useTheme,
} from "@mui/material";
import { useMedia } from "../context/MediaContext";
import { TFiat } from "../utils/types";
import AccountBalanceWalletRounded from "@mui/icons-material/AccountBalanceWalletRounded";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import { CustomDialog, EmptyState, PageHeader } from "../components";
import SettleTable from "../components/SettleTable";
import { CustomDialogHandle } from "../components/CustomDialog";
import { useBalance } from "../hooks/useBalance";
import { useAuthStore } from "../store/authStore";
import { useCurrencySymbol } from "../hooks/useCurrencyOptions";
import { Loading, Error } from "../components";
import { changeBalanceMerch } from "../utils/api";
import { getLatestTest } from "../utils/api";
import { useMerchants } from "../hooks/useMerchants";
import { Transaction } from "../utils/types";
import MerchantBalanceCard, { money } from "../components/MerchantBalanceCard";

export default function BalancePage() {
  const theme = useTheme();
  const { merchants } = useMerchants();
  const { curr } = useAuthStore();
  const [history, setHistory] = useState<boolean>(false);
  const [setleLoading, setSetleLoading] = useState<boolean>(false);
  const [settleError, setSettleError] = useState<string | null>(null);
  const { balance, error, setError, loading, getBalance } = useBalance();
  const dialogRef = useRef<CustomDialogHandle>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPage, setTotalPage] = useState<number>(0);
  const { isMobile600 } = useMedia();
  const [chosenMerch, setChosenMerch] = useState<number | null>(null);
  const [changeBalance, setChangeBalance] = useState({
    type: "",
    merchant_name: "",
    transaction_type: "",
    amount: "0.0",
    course: "0.0",
    currency: curr,
  });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const loadTransactions = async (merchid: number, page: number) => {
    setSetleLoading(true);

    const filters = {
      merchid: [merchid],
      status: ["completed"],
      transaction_type: ["withdrawal", "cashin"],
    };

    try {
      const res = await getLatestTest({ page, filters });
      const data = res.data ?? {};
      setTransactions(data.items ?? []);
      setTotalPage(data.total_pages ?? 0);
    } catch (err) {
      setSettleError("Ошибка при загрузке истории");
    } finally {
      setSetleLoading(false);
    }
  };

  useEffect(() => {
    if (!history) return;
    if (chosenMerch !== null) {
      loadTransactions(chosenMerch, page);
      window.scrollTo(0, 0);
    }
  }, [page, history]);

  const getCurrencyValue = (
    bal: (typeof balance)[string],
    currency: string,
  ): number => {
    if (!bal) return 0;
    const currLower = currency.toLowerCase();
    return (
      bal.deposit.fiat[currLower as keyof typeof bal.deposit.fiat] ??
      bal.deposit.usdt
    );
  };
  const submitChange = (data: {
    merchant_name: string;
    transaction_type: string;
    amount: string;
    course: string;
    currency: string;
  }) => {
    const { merchant_name, transaction_type, amount, course, currency } = data;
    const payload = {
      merchant_name:
        merchants.find((el) => el.id === Number(merchant_name))?.name ?? "",
      transaction_type,
      amount: Number(amount),
      course: Number(course),
      currency: String(currency.toLowerCase()),
    };
    return changeBalanceMerch(payload)
      .then(() => {
        dialogRef?.current?.closeDialog();
        getBalance();
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setChangeBalance((prev) => ({ ...prev, amount: "0.0" }));
      });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ammount: boolean = false,
  ) => {
    let value = e.target.value.replace(/\s+/g, "").replace(/,/g, ".");
    const input = e.target;
    let cursorPosition = input.selectionStart ?? 0;

    if (value === "") {
      setChangeBalance((prev) => ({ ...prev, amount: "0.0" }));
      return;
    }

    if (!value.startsWith("0.") && value.startsWith("0")) {
      value = value.replace(/^0+(?=\d)/, "");
      cursorPosition--;
    }

    const balItem = balance[changeBalance.merchant_name];
    const maxDeposit = balItem ? getCurrencyValue(balItem, curr) : 0;

    if (
      parseFloat(value) > maxDeposit &&
      changeBalance.transaction_type === "Перестановка"
    ) {
      setError("Сумма вывода не может превышать общий баланс");
    } else {
      setError("");
    }

    if (value.includes(".")) {
      const [intPart, decPart] = value.split(".");
      value = decPart ? `${intPart}.${decPart}` : `${intPart}.0`;
    } else {
      value = `${value}.0`;
    }

    if (ammount) {
      setChangeBalance((prev) => ({ ...prev, course: value }));
    } else {
      setChangeBalance((prev) => ({ ...prev, amount: value }));
    }

    requestAnimationFrame(() => {
      input.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  // Знак приходит из реестра валют: он задаётся в админке, и новая валюта
  // должна показываться со своим знаком без выката фронта.
  const currencySymbol = useCurrencySymbol(curr);

  /** Достаёт значение в выбранной валюте: USDT лежит отдельным полем от фиата. */
  const inCurrency = (bucket: { fiat: TFiat; usdt: number }) =>
    Number(
      curr.toLowerCase() === "usdt"
        ? bucket.usdt
        : (bucket.fiat[curr.toLowerCase() as keyof TFiat] ?? 0),
    );

  /** Крупные счета вперёд — админ смотрит балансы сверху вниз. */
  const accounts = useMemo(
    () =>
      Object.entries(balance)
        .map(([id, val]) => ({
          id,
          name:
            merchants.find((el) => el.id === Number(id))?.name ?? `#${id}`,
          deposit: inCurrency(val.deposit),
          payout: inCurrency(val.payout),
          frozen: inCurrency(val.frozen),
        }))
        .sort(
          (a, b) =>
            b.deposit + b.payout + b.frozen - (a.deposit + a.payout + a.frozen),
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [balance, merchants, curr],
  );

  const totals = useMemo(() => {
    const sum = (key: "deposit" | "payout" | "frozen") =>
      accounts.reduce((acc, a) => acc + a[key], 0);
    return [
      { label: "Депозитный", value: sum("deposit"), tone: theme.palette.accentText },
      {
        label: "Выплатной",
        value: sum("payout"),
        tone: theme.palette.semanticText.success,
      },
      {
        label: "Заморожено",
        value: sum("frozen"),
        tone: theme.palette.semanticText.warning,
      },
    ];
  }, [accounts, theme]);

  const openChange = (merchantId: string, type: string) => {
    setChangeBalance({
      type: "merchant",
      course: "0.0",
      amount: "0.0",
      merchant_name: merchantId,
      transaction_type: type,
      currency: curr,
    });
    dialogRef?.current?.openDialog();
  };

  const openHistory = (merchantId: string) => {
    setChosenMerch(Number(merchantId));
    setChangeBalance({
      type: "",
      merchant_name: "",
      transaction_type: "",
      amount: "0.0",
      course: "0.0",
      currency: curr,
    });
    setHistory(true);
    dialogRef?.current?.openDialog();
  };

  if (loading) return <Loading variant="cards" />;
  if (error && error !== "Сумма вывода не может превышать общий баланс") {
    return <Error error={error} />;
  }

  return (
    <Box
      sx={{
        overflow: "auto",
        width: "96%",
        display: "flex",
        flexDirection: "column",
        margin: "0 auto",
        "&::-webkit-scrollbar": { display: "none" },
        flex: 1,
      }}
      padding={"15px 0"}
    >
      <PageHeader
        title="Балансы"
        description={`Доступные, выплатные и замороженные средства в ${curr}`}
        icon={AccountBalanceWalletRounded}
      />
      {accounts.length === 0 ? (
        <EmptyState
          icon={AccountBalanceWalletOutlined}
          title="Данных о балансах нет"
          description="Баланс появится после подключения первого мерчанта."
        />
      ) : (
        <>
          {/* Сводка по всем счетам: одна плотная строка, а не сетка плиток. */}
          <Paper
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: "16px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 2, sm: 3.5 },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              {accounts.length} мерчант(ов)
            </Typography>
            {totals.map((item) => (
              <Box
                key={item.label}
                sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 11.5, fontWeight: 650 }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 720,
                    fontVariantNumeric: "tabular-nums",
                    color: item.tone,
                  }}
                >
                  {money(item.value)}
                  <Typography
                    component="span"
                    color="text.secondary"
                    sx={{ fontSize: 11, fontWeight: 700, ml: 0.4 }}
                  >
                    {currencySymbol}
                  </Typography>
                </Typography>
              </Box>
            ))}
          </Paper>

          <Box
            sx={{
              maxHeight: {
                xs: `calc(
        var(--vvh) * 0.62
        - env(safe-area-inset-top)
        - env(safe-area-inset-bottom)
      )`,
                md: "62vh",
              },
              overflow: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(340px, 1fr))"
              gap={2}
              sx={{ pb: 2 }}
            >
              {accounts.map((account) => (
                <MerchantBalanceCard
                  key={account.id}
                  name={account.name}
                  symbol={currencySymbol}
                  deposit={account.deposit}
                  payout={account.payout}
                  frozen={account.frozen}
                  onDeposit={() => openChange(account.id, "Пополнение")}
                  onWithdraw={() => openChange(account.id, "Вывод")}
                  onMoveToPayout={() => openChange(account.id, "Перестановка")}
                  onHistory={() => openHistory(account.id)}
                />
              ))}
            </Box>
          </Box>
        </>
      )}

      <CustomDialog
        ref={dialogRef}
        setData={() => setHistory(false)}
        dialog={{
          title: history
            ? "История сетлов"
            : changeBalance.transaction_type === "Вывод"
              ? "Вывод Средств"
              : changeBalance.transaction_type === "Перестановка"
                ? "Перевод средств на выплату"
                : "Пополнение средств",
          sx: { display: "flex", flexDirection: "column", padding: "15px" },
          context:
            changeBalance.transaction_type &&
            changeBalance.transaction_type !== "Пополнение"
              ? `Максимальная сумма вывода ${
                  balance[changeBalance.merchant_name]
                    ? getCurrencyValue(
                        balance[changeBalance.merchant_name],
                        curr,
                      )
                    : 0
                }`
              : null,
        }}
      >
        <Box width="100%" position="relative">
          {history && setleLoading ? (
            <Loading compact variant="table" />
          ) : history && settleError ? (
            <Error error={settleError} />
          ) : history ? (
            <>
              <SettleTable transactions={transactions} />
              <Pagination
                count={totalPage}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                variant="outlined"
                shape="rounded"
                size="large"
                siblingCount={1}
                boundaryCount={isMobile600 ? 0 : 1}
                renderItem={(item) => {
                  if (
                    item.type === "start-ellipsis" ||
                    item.type === "end-ellipsis"
                  ) {
                    return null;
                  }
                  return <PaginationItem {...item} />;
                }}
                sx={{
                  marginTop: "10px",
                  "& .MuiPaginationItem-root": {
                    
                    fontSize: "16px",
                  },
                }}
              />
            </>
          ) : (
            <>
              <TextField
                label="Сумма"
                type="text"
                onChange={handleChange}
                value={changeBalance.amount}
                error={error === "Сумма вывода не может превышать общий баланс"}
                sx={{ width: "100%", marginBottom: "20px" }}
                required
              />

              {changeBalance.transaction_type !== "Перестановка" && (
                <TextField
                  label="Курс USDT"
                  type="text"
                  onChange={(e) => handleChange(e, true)}
                  value={changeBalance.course}
                  sx={{ width: "100%", marginBottom: "20px" }}
                />
              )}

              <Button
                onClick={() => submitChange(changeBalance)}
                variant="contained"
                // Вывод уменьшает баланс — единственное здесь деструктивное
                // действие, поэтому только оно красное.
                color={
                  changeBalance.transaction_type === "Вывод"
                    ? "error"
                    : "primary"
                }
                disabled={
                  Number(changeBalance.amount) <= 0 ||
                  (changeBalance.transaction_type === "Перестановка" &&
                    Number(changeBalance.amount) >
                      getCurrencyValue(
                        balance[changeBalance.merchant_name],
                        curr,
                      ))
                }
                sx={{ width: "100%", height: 52 }}
              >
                {changeBalance.transaction_type === "Вывод"
                  ? "Вывести средства"
                  : changeBalance.transaction_type === "Перестановка"
                    ? "Перевести на выплату"
                    : "Пополнить баланс"}
              </Button>
            </>
          )}
        </Box>
      </CustomDialog>
    </Box>
  );
}
